/**
 * Navigation Helper Utility
 * Determines the next enabled section based on current state
 */

import { sharedData } from "../sharedData/sharedData";

// Corporate + Management Structure are not required when diligence is Simplified.
// Read from sharedData so every caller (StepTabs, forms, wizards) sees the same gate.
export const isDiligenceSimplified = () => {
  const d = sharedData.get("diligenceStatus");
  return d === "Simplified" || d === "simplified";
};

// Whether a section is skipped is the server's call: it returns "Not Applicable" only
// when diligence is Simplified AND the section holds no data. A Simplified section that
// someone put data into is required, and Next must not jump over it. StepTabs primes
// these from /get-all-statuses; until then we fall back to the local diligence value.
export const isSectionSkipped = (key) => {
  const statuses = sharedData.get("sectionStatuses");
  const status = statuses && statuses[key];
  if (status) return String(status).toLowerCase() === "not applicable";
  return isDiligenceSimplified();
};

// Seed diligenceStatus from the dashboard/all-requests row so that StepTabs,
// getNextEnabledSection, and diligenceObservable.isEnhanced() have the right
// value on the very first render of the wizard — not only after the user
// visits Client/Counterparty and the country+companyType fetch re-derives it.
// Dashboard keeps the backend key Deligence_status; AllRequest renames it to
// deligenceStatus during its map(); accept either.
export const primeDiligenceFromRow = (row) => {
  const d = row?.Deligence_status ?? row?.deligenceStatus ?? row?.diligenceStatus;
  if (d === "Simplified" || d === "Standard" || d === "Enhanced") {
    sharedData.set("diligenceStatus", d);
    sharedData.set("diligenceObservable", [
      { value: d, by: "persisted", isEnhanced: d === "Enhanced" },
    ]);
  }
};

/**
 * Gets the next enabled section path
 * @param {string} currentSection - Current section path (e.g., '/client-counter')
 * @param {Object} options - Configuration options
 * @param {boolean} options.isEntityIdMissing - Whether entityId is missing
 * @param {boolean} options.isClientCounterpartyCompleted - Whether Client/Counterparty is validated
 * @param {URLSearchParams} options.searchParams - Search parameters to append to path
 * @returns {string} - The next enabled section path with search params
 */
export const getNextEnabledSection = (currentSection, options = {}) => {
  const {
    isEntityIdMissing = false,
    isClientCounterpartyCompleted = false,
    searchParams = new URLSearchParams()
  } = options;
  const corporateSkipped = isSectionSkipped("corporate");
  const managementSkipped = isSectionSkipped("management");

  // Define the navigation flow
  const navigationFlow = [
    { path: '/transaction-detail', alwaysEnabled: true },
    { path: '/property-info', alwaysEnabled: true },
    { path: '/client-counter', alwaysEnabled: true },
    {
      path: '/corporate-structure',
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted || corporateSkipped
    },
    {
      path: '/management-structure',
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted || managementSkipped
    },
    {
      path: '/RiskAsses',
      disabled: isEntityIdMissing
    },
    {
      path: '/FinalReview',
      disabled: isEntityIdMissing
    }
  ];

  // Find current section index
  const currentIndex = navigationFlow.findIndex(section =>
    currentSection.includes(section.path)
  );

  if (currentIndex === -1) {
    console.warn(`⚠️ Current section ${currentSection} not found in navigation flow`);
    return `/RiskAsses?${searchParams.toString()}`;
  }

  // Find next enabled section
  for (let i = currentIndex + 1; i < navigationFlow.length; i++) {
    const section = navigationFlow[i];
    if (section.alwaysEnabled || !section.disabled) {
      return `${section.path}?${searchParams.toString()}`;
    }
  }

  // If no next section found, return the last section
  return `${navigationFlow[navigationFlow.length - 1].path}?${searchParams.toString()}`;
};

/**
 * Checks if both Corporate and Management Structure should be skipped
 * @param {boolean} isEntityIdMissing - Whether entityId is missing
 * @param {boolean} isClientCounterpartyCompleted - Whether Client/Counterparty is validated
 * @returns {boolean} - True if both structures should be skipped
 */
export const shouldSkipStructures = (isEntityIdMissing, isClientCounterpartyCompleted) => {
  return isEntityIdMissing || !isClientCounterpartyCompleted;
};

/**
 * Checks if Corporate Structure is disabled
 * @param {boolean} isEntityIdMissing - Whether entityId is missing
 * @param {boolean} isClientCounterpartyCompleted - Whether Client/Counterparty is validated
 * @returns {boolean} - True if Corporate Structure is disabled
 */
export const isCorporateStructureDisabled = (isEntityIdMissing, isClientCounterpartyCompleted) => {
  return isEntityIdMissing || !isClientCounterpartyCompleted;
};

/**
 * Checks if Management Structure is disabled
 * @param {boolean} isEntityIdMissing - Whether entityId is missing
 * @param {boolean} isClientCounterpartyCompleted - Whether Client/Counterparty is validated
 * @returns {boolean} - True if Management Structure is disabled
 */
export const isManagementStructureDisabled = (isEntityIdMissing, isClientCounterpartyCompleted) => {
  return isEntityIdMissing || !isClientCounterpartyCompleted;
};

/**
 * Gets the previous enabled section path
 * @param {string} currentSection - Current section path (e.g., '/RiskAsses')
 * @param {Object} options - Configuration options
 * @param {boolean} options.isEntityIdMissing - Whether entityId is missing
 * @param {boolean} options.isClientCounterpartyCompleted - Whether Client/Counterparty is validated
 * @param {URLSearchParams} options.searchParams - Search parameters to append to path
 * @returns {string} - The previous enabled section path with search params
 */
export const getPreviousEnabledSection = (currentSection, options = {}) => {
  const {
    isEntityIdMissing = false,
    isClientCounterpartyCompleted = false,
    searchParams = new URLSearchParams()
  } = options;
  const corporateSkipped = isSectionSkipped("corporate");
  const managementSkipped = isSectionSkipped("management");

  // Define the navigation flow
  const navigationFlow = [
    { path: '/transaction-detail', alwaysEnabled: true },
    { path: '/property-info', alwaysEnabled: true },
    { path: '/client-counter', alwaysEnabled: true },
    {
      path: '/corporate-structure',
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted || corporateSkipped
    },
    {
      path: '/management-structure',
      disabled: isEntityIdMissing || !isClientCounterpartyCompleted || managementSkipped
    },
    {
      path: '/RiskAsses',
      disabled: isEntityIdMissing
    },
    {
      path: '/FinalReview',
      disabled: isEntityIdMissing
    }
  ];

  // Find current section index
  const currentIndex = navigationFlow.findIndex(section =>
    currentSection.includes(section.path)
  );

  if (currentIndex === -1) {
    console.warn(`⚠️ Current section ${currentSection} not found in navigation flow`);
    return `/client-counter?${searchParams.toString()}`;
  }

  // Find previous enabled section (go backwards)
  for (let i = currentIndex - 1; i >= 0; i--) {
    const section = navigationFlow[i];
    if (section.alwaysEnabled || !section.disabled) {
      return `${section.path}?${searchParams.toString()}`;
    }
  }

  // If no previous section found, return the first section
  return `${navigationFlow[0].path}?${searchParams.toString()}`;
};
